// milsymbolSettings.js — symbol render settings context, now backed by mil-sym-ts RendererSettings.
// Exports RendererSettingsProvider and useRendererSettings().
// Also exports legacy aliases (MilsymbolSettingsProvider, useMilsymbolSettings)
// so existing import sites don't all need simultaneous updates.

import React, {createContext, useContext, useState, useCallback, useMemo, useEffect} from 'react';
import {RendererSettings} from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import {ensureInit} from './renderer/SymbolRenderer';

// Re-export init so app boot can call it once
export {ensureInit as initRenderer};

export const DEFAULT_SYMBOL_SETTINGS = {
    textBackgroundMethod:   1,  // RendererSettings.TextBackgroundMethod_COLORFILL (default)
    autoCollapseModifiers:  false,
    ocmType:                0,  // RendererSettings.OperationalConditionModifierType_SLASH
    outlineWidth:           2,  // setDefaultSPSymbolOutlineWidth
    centerOnHQStaff:        true,
    spModifierPlacement:    1,  // RendererSettings.ModifierPlacement_FLEXIBLE
};

// Apply settings to the RendererSettings singleton
function _applyToRenderer(settings) {
    try {
        const rs = RendererSettings.getInstance();
        if (settings.textBackgroundMethod != null)  rs.setTextBackgroundMethod(settings.textBackgroundMethod);
        if (settings.autoCollapseModifiers != null) rs.setAutoCollapseModifiers(settings.autoCollapseModifiers);
        if (settings.ocmType != null)               rs.setOperationalConditionModifierType(settings.ocmType);
        if (settings.outlineWidth != null)          rs.setSinglePointSymbolOutlineWidth(settings.outlineWidth);
        if (settings.centerOnHQStaff != null)       rs.setCenterOnHQStaff(settings.centerOnHQStaff);
        if (settings.spModifierPlacement != null)   rs.setSPModifierPlacement(settings.spModifierPlacement);
    } catch (_) { /* renderer may not be ready yet */ }
}

const RendererSettingsContext = createContext({
    settings:       DEFAULT_SYMBOL_SETTINGS,
    updateSettings: () => {},
});

export function RendererSettingsProvider({children}) {
    const [settings, setSettings] = useState(DEFAULT_SYMBOL_SETTINGS);

    // Initialise the renderer once on mount
    useEffect(() => {
        ensureInit().catch((e) => {
            console.warn('[milsymbolSettings] init failed', e);
        });
    }, []);

    const updateSettings = useCallback((patch) => {
        setSettings((prev) => {
            const next = { ...prev, ...patch };
            _applyToRenderer(next);
            return next;
        });
    }, []);

    const value = useMemo(() => ({ settings, updateSettings }), [settings, updateSettings]);
    return (
        <RendererSettingsContext.Provider value={value}>
            {children}
        </RendererSettingsContext.Provider>
    );
}

export function useRendererSettings() {
    return useContext(RendererSettingsContext);
}

// ── Legacy aliases — avoid breaking existing import sites ──────────────────
export const MilsymbolSettingsProvider = RendererSettingsProvider;
export const useMilsymbolSettings      = useRendererSettings;

/** Shallow-merge per-symbol overrides onto context settings. */
export function buildSymbolOptions(settings, overrides = {}) {
    return { ...settings, ...overrides };
}
